/**
 * Camada 0 · o campo de ANEXO DE IMAGEM, e ele nasce das duas frases dele de 07/09/2026:
 *
 *   "TEM QUE PODER COLOCAR ANEXO, QQ TIPO DE IMAGEM DE QQ TAMANHO."
 *   "NAO QUERO GERAR NENHUM TIPO DE RESTRICAO PARA USUARIO."
 *
 * TRES DECISOES QUE VEM DIRETO DESSAS FRASES:
 *
 * 1. `accept="image/*"` E NAO UMA LISTA. Uma lista sempre esquece um: o `accept` que ja
 *    existia no admin deixava de fora HEIC (todo iPhone) e WEBP (todo Android), e a pessoa
 *    ficava com a foto acinzentada no seletor sem entender por que.
 *
 * 2. NENHUM AVISO DE TAMANHO, e nenhuma recusa. `reduzirImagemParaAnexo` reduz ate caber
 *    antes de qualquer envio. A pessoa escolhe a foto e ela entra: nao ha limite escrito na
 *    tela porque nao ha limite que ela alcance.
 *
 * 3. O `<input type="file">` FICA INVISIVEL e o clique passa pelo `Botao` da plataforma.
 *    Nao e enfeite: o input nativo desenha diferente em cada sistema operacional, e o
 *    `guarda-fronteira.sh` recusa `<button>` cru em app justamente para o chassi nao virar
 *    seis chassis. `sr-only` mantem o input alcancavel por teclado e por leitor de tela.
 *
 * POR QUE ELE NAO ENVIA NADA: quem envia e quem sabe PARA ONDE. A Ajuda fala com o
 * services/messaging pelo transporte do produto; o Super Admin fala com o admin-api, com
 * trilha a cada travessia. Sao dois caminhos de autorizacao diferentes, e um componente que
 * escolhesse um deles carregaria a fronteira de seguranca para dentro de si. Ele entrega o
 * base64 pronto, e o chamador grava.
 */

import { t } from '@complexity/i18n';
import { useCallback, useRef, useState } from 'react';
import { Botao } from './Button.js';
import { reduzirImagemParaAnexo, type ImagemReduzida } from '../imagem.js';

export interface CampoAnexoProps {
  /** As imagens ja escolhidas. O estado mora em quem chama, porque e ele quem envia. */
  readonly anexos: readonly ImagemReduzida[];
  /** Chamado com a lista NOVA, ja reduzida. */
  readonly aoMudar: (anexos: readonly ImagemReduzida[]) => void;
  /** Trava o campo enquanto o formulario envia. */
  readonly desabilitado?: boolean;
  /** O texto do botao. O padrao serve aos dois lados. */
  readonly rotulo?: string;
}

/** Quantos bytes em palavra de gente. */
function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function CampoAnexo({
  anexos,
  aoMudar,
  desabilitado = false,
  rotulo = 'Anexar imagem',
}: CampoAnexoProps) {
  const entrada = useRef<HTMLInputElement | null>(null);
  const [processando, definirProcessando] = useState(false);
  const [falha, definirFalha] = useState<string | null>(null);

  const escolher = useCallback(
    async (lista: FileList | null) => {
      if (!lista || lista.length === 0) return;
      definirProcessando(true);
      definirFalha(null);
      const novos: ImagemReduzida[] = [];
      const recusados: string[] = [];
      for (const arquivo of Array.from(lista)) {
        try {
          novos.push(await reduzirImagemParaAnexo(arquivo));
        } catch {
          // A UNICA recusa possivel e "nao consegui LER como imagem", e nunca tamanho.
          // Ela e por ARQUIVO: um arquivo ilegivel no meio de tres nao pode derrubar os
          // outros dois, e a pessoa precisa saber QUAL nao entrou.
          recusados.push(arquivo.name);
        }
      }
      if (novos.length > 0) aoMudar([...anexos, ...novos]);
      if (recusados.length > 0) {
        definirFalha(
          recusados.length === 1
            ? `Não consegui ler "${recusados[0]}" como imagem.`
            : `Não consegui ler ${recusados.length} dos arquivos como imagem.`,
        );
      }
      definirProcessando(false);
      // O input e LIMPO depois de ler: sem isso, escolher o MESMO arquivo de novo nao
      // dispara `change`, e a pessoa clica e nada acontece.
      if (entrada.current) entrada.current.value = '';
    },
    [anexos, aoMudar],
  );

  const remover = useCallback(
    (indice: number) => aoMudar(anexos.filter((_, i) => i !== indice)),
    [anexos, aoMudar],
  );

  return (
    <div className="flex flex-col gap-cx-2">
      <div className="flex flex-wrap items-center gap-cx-2">
        <Botao
          variante="secundario"
          tamanho="sm"
          type="button"
          disabled={desabilitado || processando}
          onClick={() => entrada.current?.click()}
        >
          {processando ? t('Preparando…') : t(rotulo)}
        </Botao>

        {/*
          O INPUT E INVISIVEL E NAO REMOVIDO: `sr-only` o mantem no fluxo de foco e na
          arvore acessivel. Com `display:none` ele sai dos dois, e quem navega por teclado
          perde o anexo inteiro.

          `multiple` porque quem descreve um problema costuma ter mais de uma captura, e
          obriga-la a repetir o gesto tres vezes e atrito que nao resolve nada.
        */}
        <input
          ref={entrada}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          disabled={desabilitado || processando}
          onChange={(evento) => void escolher(evento.target.files)}
        />
      </div>

      {falha ? <p className="text-cx-sm text-erro">{falha}</p> : null}

      {anexos.length > 0 ? (
        <ul className="flex flex-col gap-cx-1">
          {anexos.map((a, i) => (
            <li
              key={`${a.nomeArquivo}-${i}`}
              className="flex flex-wrap items-center justify-between gap-cx-2 rounded-cx-md border border-borda bg-superficie px-cx-3 py-cx-2"
            >
              <span className="text-cx-sm text-texto">{a.nomeArquivo}</span>
              <span className="flex items-center gap-cx-2">
                <span className="text-cx-xs text-texto-fraco">
                  {tamanhoLegivel(a.tamanhoBytes)}
                  {/*
                    O "reduzida de X" so aparece quando REDUZIU, e nao sempre. Dizer sempre
                    faria a pessoa procurar uma diferenca que nao existe no caso comum, em
                    que o arquivo passou intacto.
                  */}
                  {a.tamanhoBytes < a.tamanhoOriginalBytes
                    ? ` · reduzida de ${tamanhoLegivel(a.tamanhoOriginalBytes)}`
                    : ''}
                </span>
                <Botao
                  variante="texto"
                  tamanho="sm"
                  type="button"
                  disabled={desabilitado}
                  onClick={() => remover(i)}
                >
                  Remover
                </Botao>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Reexporta o tipo para quem monta a tela nao precisar importar de dois lugares. */
export type { ImagemReduzida };
